import { SVGPathData, encodeSVGPath } from 'svg-pathdata';
import { Vector2 } from '@/utilityModels/vector2';
import { Rectangle } from '@/utilityModels/rectangle';
import { Piece } from '@/data/template/detail/sheet/pieces/piece/piece';

export class PathHelper
{
    // 多角形の初期コントロールポイントを作成する
    // cornerNumber : 角数(3〜)、ただし0を指定すると矢印を作成、1を指定すると星を作成
    // x, y, width, height : 指定サイズ、この大きさに収まるように作成する
    // autoHeight : trueのとき、正多角形になるように高さを自動変更する
    // ---
    // return : Array<Vector2>
    public static createPolygonControlPoints(cornerNumber : number, x : number, y : number, width : number, height : number, autoHeight : boolean = false)
    {
        // 0指定は矢印
        var isArrow = cornerNumber == 0;
        // 1指定は矢印
        var isStar = cornerNumber == 1;

        var points = new Array<Vector2>();

        if(isArrow)
        {
            // 矢印
            // 各頂点の基準点
            // x = 0, y = 0, width = 100, height = 80
            points.push(new Vector2(0, 20));
            points.push(new Vector2(0, 60));
            points.push(new Vector2(60, 60));
            points.push(new Vector2(60, 80));
            points.push(new Vector2(100, 40));
            points.push(new Vector2(60, 0));
            points.push(new Vector2(60, 20));

            // スケーリングとオフセットを行う
            var fixHeight = autoHeight ? width : height;
            for(var point of points)
            {
                point.x = point.x * width / 100 + x;
                point.y = point.y * fixHeight / 80 + y;
            }
        }
        else if(isStar)
        {
            // 多角形
            // 正五角形に内接する星を求める
            // 星の外側の点がその円周上にある円の半径(=1とする)
            var outR = 1;
            // 星の内側の点がその円周上にある円の半径
            var inR = Math.sin(18 * Math.PI / 180) / Math.cos(36 * Math.PI / 180);
            // 円周の中心
            var cx = Math.sin(Math.PI * 2 / 5);
            var cy = 1;
            // 始点
            var sx = cx;
            var sy = 0;
            // 内側の点の始点
            var inSx = cx;
            var inSy = outR - inR;

            for(var i = 0; i < 5; ++i)
            {
                // 始点をradだけ回転した点を追加
                var rad = Math.PI * 2 / 5 * i;
                var start = new Vector2(sx, sy);
                var rotated = start.rotate(rad, cx, cy);
                points.push(new Vector2(rotated.x, rotated.y));

                // 内側の点の始点をinRadだけ回転した点を追加
                var inRad = rad + Math.PI * 2 / 10;
                var inStart = new Vector2(inSx, inSy);
                var inRotated = inStart.rotate(inRad, cx, cy);
                points.push(new Vector2(inRotated.x, inRotated.y));
            }

            // スケーリングするために、すべての点の外接矩形を求める
            var bounds = this.getBoundsFromPoints(points);

            if(bounds.width == 0 || bounds.height == 0)
            {
                points = new Array();
            }
            else
            {
                // スケーリングとオフセット
                var fixHeight = autoHeight ? width : height;
                for(var point of points)
                {
                    point.x = (point.x - bounds.x) * width / bounds.width + x;
                    point.y = (point.y - bounds.y) * fixHeight / bounds.height + y;
                }
            }
        }
        else if(cornerNumber >= 3)
        {
            // 通常の凸多角形
            // 外接円の半径(=1とする)
            var outR = 1;
            // 円周の中心
            var cx = 1;
            var cy = 1;
            // 始点
            var sx = 1;
            var sy = 0;
            for(var i = 0; i < cornerNumber; ++i)
            {
                // 始点をradだけ回転した点を追加
                var rad = Math.PI * 2 / cornerNumber * i;
                var start = new Vector2(sx, sy);
                var rotated = start.rotate(rad, cx, cy);
                points.push(new Vector2(rotated.x, rotated.y));
            }
            
            // スケーリングするために、すべての点の外接矩形を求める
            var bounds = this.getBoundsFromPoints(points);

            if(bounds.width == 0 || bounds.height == 0)
            {
                points = new Array();
            }
            else
            {
                // スケーリングとオフセット
                var scaleW = width / bounds.width;
                var scaleH = autoHeight ? scaleW : height / bounds.height;
                for(var point of points)
                {
                    point.x = (point.x - bounds.x) * scaleW + x;
                    point.y = (point.y - bounds.y) * scaleH + y;
                }
            }
        }

        return points;
    }

    // 多角形を作成する
    public static createPolygon(points : Array<Vector2>, radius : number = 0)
    {
        // ３点ないと多角形にならない。
        if(points.length < 3) { return ""; }

        var svgPath = "";
        var commands = new Array();

        for(var i = 0; i < points.length; ++i)
        {
            var point = points[i];

            if(radius > 0)
            {
                // 丸みあり
                // 2次ベジェ曲線を作成する
                // 一つ前の点のインデックス
                var beforeIndex = i - 1;
                if(beforeIndex < 0) { beforeIndex = points.length - 1; }
                // 一つ前の点
                var before = points[beforeIndex];
                // 一つ先の点のインデックス
                var nextIndex = i + 1;
                if(nextIndex >= points.length) { nextIndex = 0; }
                // 一つ先の点
                var next = points[nextIndex]; 

                // 一つ前の点との距離
                var distanceToBefore = this.getDistance(point, before);
                // 一つ先の点との距離
                var distanceToNext = this.getDistance(point, next);

                // 曲線の開始座標
                var curveStart = this.getPointOnLine(point, before, distanceToBefore * radius / 200);
                // 曲線の終了座標
                var curveEnd = this.getPointOnLine(point, next, distanceToNext * radius / 200);
                
                if(i == 0)
                {
                    commands.push({type: SVGPathData.MOVE_TO, relative: false, x: curveStart.x, y: curveStart.y });
                }
                else
                {
                    commands.push({type: SVGPathData.LINE_TO, relative: false, x: curveStart.x, y: curveStart.y });
                }
                commands.push({type: SVGPathData.QUAD_TO, relative: false, x1: point.x, y1: point.y, x: curveEnd.x, y: curveEnd.y });
            }
            else
            {
                // 丸みなし
                if(i == 0)
                {
                    commands.push({type: SVGPathData.MOVE_TO, relative: false, x: point.x, y: point.y });
                }
                else
                {
                    commands.push({type: SVGPathData.LINE_TO, relative: false, x: point.x, y: point.y });
                }
            }
        }

        commands.push({type: SVGPathData.CLOSE_PATH});
        var pathData = new SVGPathData(commands);
        svgPath = pathData.encode();

        return svgPath;
    }

    // ２点間の距離を返す
    public static getDistance(p1 : Vector2, p2 : Vector2)
    {
        return Math.sqrt((p2.x - p1.x) * (p2.x - p1.x) + (p2.y - p1.y) * (p2.y - p1.y));
    }

    // ある点から距離rにあり、指定の点を通るように伸ばした半直線上にある点を求める
    public static getPointOnLine(p : Vector2, toP : Vector2, r : number)
    {
        // p - toP　へのベクトル
        var v = new Vector2(toP.x - p.x, toP.y - p.y);
        if(v.x == 0 && v.y == 0)
        {
            return new Vector2(p.x, p.y);
        }
        // 求める点へのベクトルを t * v としたときのtは以下のように求まる
        var t = r / Math.sqrt(v.x * v.x + v.y * v.y)
        // 点を求める
        return new Vector2(p.x + t * v.x, p.y + t * v.y);
    }

    // パスから図形を逆算して取得する
    public static matchPattern(path : string) : { pattern : string, cornerNumber : number }
    {
        if(path == "") { return { pattern : "rect", cornerNumber : 0 }; }

        var pattern = 0;
        var cornerNumber = 0;
        while(pattern <= 5)
        {
            var cornerMin = pattern == 5 ? 3 : 0;
            var cornerMax = pattern == 5 ? 8 : 0;
            
            cornerNumber = cornerMin;
            while(cornerNumber <= cornerMax)
            {
                var outHeight = { height : 0 };
                var findPath = this.createPattern(pattern, cornerNumber, 0, 0, 100, 100, 0, 0, 0, false, outHeight);
                if(path == findPath)
                {
                    var patternStr = "rect";
                    if(pattern == 1) { patternStr = "circle"; }
                    else if(pattern == 3) { patternStr = "star"; }
                    else if(pattern == 4) { patternStr = "arrow"; }
                    else if(pattern == 5) { patternStr = "polygon"; }
                    return { pattern : patternStr, cornerNumber : cornerNumber};
                }

                cornerNumber++;
            }

            pattern++;
            // 三角形は多角形で調べるので飛ばす
            if(pattern == 2) { pattern++; }
        }

        return { pattern : "rect", cornerNumber : 0 };
    }


    // 図形を作成する
    // 図形種類
    // 0: 矩形
    // 1: 円
    // 2: 三角形
    // 3: 星
    // 4: 矢印
    // 5: 多角形
    // autoHeight : trueのとき、パスを規定の縦横比(正円・正多角形)で作る
    // outHeight : autoHeight == true のときの高さをオブジェクトで返す。(オブジェクトなのは参照渡しにするため)
    public static createPattern(pattern : number, cornerNumber : number, 
        x : number, y : number, width : number, height : number, hRadius : number, vRadius : number, radiusRate : number,
        autoHeight : boolean = false, outHeight : { height : number } = { height : 0 } ) : string
    {
        var svgPath = "";

        if(pattern == 0)
        {
            var heightFix = autoHeight ? width : height;
            outHeight.height = heightFix;

            if(hRadius > 0 && vRadius > 0)
            {
                svgPath = encodeSVGPath(
                    [
                        { type: SVGPathData.MOVE_TO, relative: false, x: x + hRadius, y: y },
                        { type: SVGPathData.LINE_TO, relative: false, x: x + width - hRadius, y: y },
                        { type: SVGPathData.ARC, relative: false, rX: hRadius, rY: vRadius, xRot: 0, lArcFlag: 0, sweepFlag: 1, x: x + width, y: y + vRadius },
                        { type: SVGPathData.LINE_TO, relative: false, x: x + width, y: y + heightFix - vRadius },
                        { type: SVGPathData.ARC, relative: false, rX: hRadius, rY: vRadius, xRot: 0, lArcFlag: 0, sweepFlag: 1, x: x + width - hRadius, y: y + heightFix },
                        { type: SVGPathData.LINE_TO, relative: false, x: x + hRadius, y: y + heightFix },
                        { type: SVGPathData.ARC, relative: false, rX: hRadius, rY: vRadius, xRot: 0, lArcFlag: 0, sweepFlag: 1, x: x, y: y + heightFix - vRadius },
                        { type: SVGPathData.LINE_TO, relative: false, x: x, y: y + vRadius },
                        { type: SVGPathData.ARC, relative: false, rX: hRadius, rY: vRadius, xRot: 0, lArcFlag: 0, sweepFlag: 1, x: x + hRadius, y: y },
                        { type: SVGPathData.CLOSE_PATH }
                    ]
                );
            }
            else
            {
                svgPath = encodeSVGPath(
                    [
                        { type: SVGPathData.MOVE_TO, relative: false, x: x, y: y },
                        { type: SVGPathData.LINE_TO, relative: false, x: x + width, y: y },
                        { type: SVGPathData.LINE_TO, relative: false, x: x + width, y: y + heightFix },
                        { type: SVGPathData.LINE_TO, relative: false, x: x, y: y + heightFix },
                        { type: SVGPathData.CLOSE_PATH }
                    ]
                );
            }
        }
        else if(pattern == 1)
        {
            // 円
            var heightFix = autoHeight ? width : height;
            outHeight.height = heightFix;
            svgPath = encodeSVGPath(
                [
                    { type: SVGPathData.MOVE_TO, relative: false, x: x + width, y: y + heightFix / 2 },
                    { type: SVGPathData.ARC, relative: false, rX: width / 2, rY: heightFix / 2, xRot: 0, lArcFlag: 0, sweepFlag: 1, x: x, y: y + heightFix / 2 },
                    { type: SVGPathData.ARC, relative: false, rX: width / 2, rY: heightFix / 2, xRot: 0, lArcFlag: 0, sweepFlag: 1, x: x + width, y: y + heightFix / 2 },
                    { type: SVGPathData.CLOSE_PATH }   
                ]
            );
        }
        else if(pattern == 3)
        {
            // 星
            // いったん正五角形に内接する星を求めてから、スケーリングする。
            
            // 星の外側の点がその円周上にある円の半径
            var outR = 1;
            // 星の内側の点がその円周上にある円の半径
            var inR = Math.sin(18 * Math.PI / 180) / Math.cos(36 * Math.PI / 180);
            // 円周の中心
            var cx = Math.sin(Math.PI * 2 / 5);
            var cy = 1;
            // 始点
            var sx = cx;
            var sy = 0;
            // 内側の点の始点
            var inSx = cx;
            var inSy = outR - inR;
            var commands = new Array();
            for(var i = 0; i < 5; ++i)
            {
                var rad = Math.PI * 2 / 5 * i;
                // 始点をradだけ回転する。
                var start = new Vector2(sx, sy);
                var rotated = start.rotate(rad, cx, cy);
                if(i == 0)
                {
                    commands.push({type: SVGPathData.MOVE_TO, relative: false, x: rotated.x, y: rotated.y });
                }
                else
                {
                    commands.push({type: SVGPathData.LINE_TO, relative: false, x: rotated.x, y: rotated.y });
                }
                var inRad = rad + Math.PI * 2 / 10;
                // 内側の点の始点をinRadだけ回転する。
                var inStart = new Vector2(inSx, inSy);
                var inRotated = inStart.rotate(inRad, cx, cy);
                commands.push({type: SVGPathData.LINE_TO, relative: false, x: inRotated.x, y: inRotated.y });
            }
            commands.push({ type: SVGPathData.CLOSE_PATH });
            var tempData = new SVGPathData(commands);
            var baseWidth = tempData.getBounds().maxX - tempData.getBounds().minX;
            var baseHeight = tempData.getBounds().maxY - tempData.getBounds().minY;
            var widthScale = width / baseWidth;
            var heightScale = autoHeight ? widthScale : height / baseHeight;
            tempData = tempData.scale(widthScale, heightScale);
            tempData = tempData.translate(x, y);
            outHeight.height = tempData.getBounds().maxY - tempData.getBounds().minY;
            svgPath = tempData.encode();
        }
        else if(pattern == 4)
        {
            // 矢印
            // いったん規定の大きさで作成してから、スケーリングする。

            var tempPath = encodeSVGPath(
                [
                    { type: SVGPathData.MOVE_TO, relative: false, x: 0, y: 20 },
                    { type: SVGPathData.LINE_TO, relative: false, x: 0, y: 60 },
                    { type: SVGPathData.LINE_TO, relative: false, x: 60, y: 60 },
                    { type: SVGPathData.LINE_TO, relative: false, x: 60, y: 80 },
                    { type: SVGPathData.LINE_TO, relative: false, x: 100, y: 40 },
                    { type: SVGPathData.LINE_TO, relative: false, x: 60, y: 0 },
                    { type: SVGPathData.LINE_TO, relative: false, x: 60, y: 20 },
                    { type: SVGPathData.CLOSE_PATH }
                ]
            );

            var tempData = new SVGPathData(tempPath);
            var baseWidth = tempData.getBounds().maxX - tempData.getBounds().minX;
            var baseHeight = tempData.getBounds().maxY - tempData.getBounds().minY;
            var widthScale = width / baseWidth;
            var heightScale = autoHeight ? widthScale : height / baseHeight;
            tempData = tempData.scale(widthScale, heightScale);
            tempData = tempData.translate(x, y);
            outHeight.height = tempData.getBounds().maxY - tempData.getBounds().minY;
            svgPath = tempData.encode();
        }
        else if(pattern == 5 || pattern == 2)
        {
            // 三角形指定の場合
            if(pattern == 2) { cornerNumber = 3; }
            // 多角形
            // いったん正N角形に内接する星を求めてから、スケーリングする。
            // 外接円の半径
            var outR = 1;
            // 一辺の長さ
            var l = outR * 2 * Math.sin(Math.PI / cornerNumber);
            // 丸み
            var radius = l / 2 * radiusRate / 100;
            // 円周の中心
            var cx = 1;
            var cy = 1;
            // 始点
            var sx = 1;
            var sy = 0;
            // 角の丸みを作る円の中心座標の始点
            var rCx = cx;
            var rCy = radius / Math.sin(Math.PI / cornerNumber);
            // 角の丸みの円弧の中心
            var rMx = cx;
            var rMy = rCy - radius;
            var commands = new Array();
            for(var i = 0; i < cornerNumber; ++i)
            {
                var rad = Math.PI * 2 / cornerNumber * i;
                // 始点をradだけ回転する。
                var start = new Vector2(sx, sy);
                var rotated = start.rotate(rad, cx, cy);
                if(radius > 0)
                {
                    // 丸みを作るための始点・終点を算出
                    var rCenter = new Vector2(rCx, rCy);
                    var rMid = new Vector2(rMx, rMy);
                    var rCenterRotated = rCenter.rotate(rad, cx, cy);
                    var rMidRotated = rMid.rotate(rad, cx, cy);
                    var arcStart = rMidRotated.rotate(-Math.PI / cornerNumber, rCenterRotated.x, rCenterRotated.y);
                    var arcEnd = rMidRotated.rotate(Math.PI / cornerNumber, rCenterRotated.x, rCenterRotated.y);
                        
                    if(i == 0)
                    {
                        commands.push({type: SVGPathData.MOVE_TO, relative: false, x: arcStart.x, y: arcStart.y });
                        commands.push({type: SVGPathData.ARC, relative: false, rX: radius, rY: radius, xRot: 0, lArcFlag: 0, sweepFlag: 1, x: arcEnd.x, y: arcEnd.y });
                    }
                    else
                    {
                        commands.push({type: SVGPathData.LINE_TO, relative: false, x: arcStart.x, y: arcStart.y });
                        commands.push({type: SVGPathData.ARC, relative: false, rX: radius, rY: radius, xRot: 0, lArcFlag: 0, sweepFlag: 1, x: arcEnd.x, y: arcEnd.y });
                    }
                }
                else
                {
                    if(i == 0)
                    {
                        commands.push({type: SVGPathData.MOVE_TO, relative: false, x: rotated.x, y: rotated.y });
                    }
                    else
                    {
                        commands.push({type: SVGPathData.LINE_TO, relative: false, x: rotated.x, y: rotated.y });
                    }
                }
            }
            commands.push({ type: SVGPathData.CLOSE_PATH });
            var tempData = new SVGPathData(commands);
            var baseWidth = tempData.getBounds().maxX - tempData.getBounds().minX;
            var baseHeight = tempData.getBounds().maxY - tempData.getBounds().minY;
            tempData = tempData.translate(-tempData.getBounds().minX, -tempData.getBounds().minY);
            var widthScale = width / baseWidth;
            var heightScale = autoHeight ? widthScale : height / baseHeight;
            tempData = tempData.scale(widthScale, heightScale);
            tempData = tempData.translate(x, y);
            outHeight.height = tempData.getBounds().maxY - tempData.getBounds().minY;
            svgPath = tempData.encode();
        }

        return svgPath;
    }

    // 直線を作成する
    public static createLine(x1 : number, y1 : number, x2 : number, y2 : number)
    {
        var svgPath = encodeSVGPath(
            [
                { type: SVGPathData.MOVE_TO, relative: false, x: x1, y: y1 },
                { type: SVGPathData.LINE_TO, relative: false, x: x2, y: y2 }
            ]
        );
        return svgPath;
    }

    // CatmullRomスプライン曲線を作成する
    public static createCatmullRomSpline(points : Array<Vector2>)
    {
        var pointArray = new Array();
        for(var i = 0; i < points.length; ++i)
        {
            pointArray.push([points[i].x, points[i].y]);
        }

        var spline = require('@yr/catmull-rom-spline');
        var splinePoints = spline.points(pointArray);
        var svgPath = spline.svgPath(splinePoints);

        return svgPath;
    }

    // 点の外接矩形を求める
    public static getBoundsFromPoints(points :Array<Vector2>) : Rectangle
    {
        var rect = new Rectangle();
        
        var minX = 0;
        var minY = 0;
        var maxX = 0;
        var maxY = 0;
        for(var i = 0; i < points.length; ++i)
        {
            var point = points[i];
            minX = i == 0 ? point.x : Math.min(point.x, minX);
            minY = i == 0 ? point.y : Math.min(point.y, minY);
            maxX = i == 0 ? point.x : Math.max(point.x, maxX);
            maxY = i == 0 ? point.y : Math.max(point.y, maxY);
        }

        rect.x = minX;
        rect.y = minY;
        rect.width = maxX - minX;
        rect.height = maxY - minY;

        return rect;
    }

    // 特殊図形の図形情報を得る
    public static createSpecialPattern(patternType : string) : { path : string, polygon : string, translateX : number, translateY : number, baseWidth : number, baseHeight : number }
    {
        var path = "";
        var polygon = "";
        var translateX = 0;
        var translateY = 0;
        var baseWidth = 1;
        var baseHeight = 1;

        if(patternType == "uTurn")
        {
            path = "M469.893482,225.896661 C469.860095,219.319555 464.529412,214 457.952305,214 C451.375199,214 446.033386,219.319555 446,225.885533 L446,236.124006 L451.675676,236.124006 L451.642289,225.885533 C451.675676,222.435612 454.480127,219.642289 457.941176,219.642289 C461.402226,219.642289 464.206677,222.435612 464.240064,225.885533 L464.206677,236.057234 L460.868045,236.057234 L467.055644,242 L473.243243,236.057234 L469.893482,236.057234 L469.893482,225.896661 Z";
            translateX = -445;
            translateY = -213;
            baseWidth = 31;
            baseHeight = 31;
        }
        else if(patternType == "cross")
        {
            polygon = "526 215.601877 522.38639 212 510.994134 223.392257 499.601877 212 496 215.601877 507.392257 226.994134 496 238.38639 499.601877 242 510.994134 230.607743 522.38639 242 526 238.38639 514.607743 226.994134";   
            translateX = -494;
            translateY = -210;
            baseWidth = 34;
            baseHeight = 34;
        }
        else if(patternType == "heart")
        {
            path = "M579.398178,222.649266 C576.351205,236.066572 563.546203,242.025548 563.250505,242 C562.954807,242.025548 549.571266,235.272042 547.167114,222.649266 C546.048605,216.779995 550.741201,212 555.382371,212 C558.995027,212 562.067714,214.268255 563.250505,217.45919 C564.433296,214.28107 567.505982,212 571.118639,212 C575.746952,212 580.72239,216.81844 579.398178,222.649266 Z";
            translateX = -546;
            translateY = -211;
            baseWidth = 35;
            baseHeight = 35;
        }
        else if(patternType == "cloud")
        {
            path = "M370.633347,276.588225 C370.857784,275.887331 370.984029,275.144384 370.984029,274.359383 C370.984029,270.2942 367.687619,267 363.619709,267 C361.19299,267 359.046816,268.177501 357.700198,269.971789 C356.858561,268.485895 355.245424,267.476608 353.407851,267.476608 C351.191541,267.476608 349.311886,268.934466 348.694686,270.95304 C348.035404,270.644647 347.291958,270.448397 346.492403,270.448397 C343.757084,270.448397 341.554802,272.663221 341.554802,275.382688 C341.554802,275.76117 341.596884,276.139653 341.681047,276.504118 C341.442584,276.462064 341.190093,276.448046 340.937602,276.448046 C338.21631,276.448046 336,278.648852 336,281.382337 C336,284.101805 338.202283,286.316629 340.937602,286.316629 C341.246202,286.316629 341.540774,286.288593 341.82132,286.232521 C342.396438,288.307167 344.304148,289.835115 346.56254,289.835115 C347.670695,289.835115 348.708713,289.47065 349.522295,288.839846 C350.602395,290.507973 352.482051,291.615385 354.614197,291.615385 C356.269415,291.615385 357.756307,290.956545 358.850434,289.891186 C359.74818,290.59208 360.88439,291.012616 362.11879,291.012616 C364.222882,291.012616 366.046428,289.793061 366.916119,288.012791 C367.252773,288.068863 367.589428,288.096899 367.94011,288.096899 C371.292629,288.096899 374,285.391449 374,282.041178 C374.013921,279.644121 372.639248,277.583494 370.633347,276.588225 Z";
            translateX = -335;
            translateY = -266;
            baseWidth = 40;
            baseHeight = 27;
        }
        else if(patternType == "explosion")
        {
            polygon = "423.518898 267.794488 420.332283 272.671654 427.393701 273.285039 420.466929 277.952756 427.707874 281.169291 419.494488 283.26378 423.518898 289.008661 415.948819 287.138583 419.943307 295.965354 411.011811 290.594488 409.620472 295.561417 406.179528 290.803937 402.67874 296.159843 400.883465 292.180315 392.355906 297.940157 395.976378 288.903937 391.622835 289.233071 394.495276 285.328346 387.089764 283.577953 394.181102 280.750394 390.33622 276.337008 395.916535 276.022835 391.98189 269.455118 397.412598 270.861417 393.941732 260.23937 402.738583 267.315748 403.711024 260.059843 408.633071 267.181102 411.281102 261.511024 413.674803 267.211024 420.781102 261.032283 417.86378 269.111024";
            translateX = -384;
            translateY = -258;
            baseWidth = 46;
            baseHeight = 42;
        }
        else if(patternType == "rectangleBalloon")
        {
            path = "M474.60343,264 L442.762262,264 C442.334326,264 442,264.352304 442,264.772358 L442,286.940379 C442,287.373984 442.347699,287.712737 442.762262,287.712737 L448.793496,287.712737 L452.377466,294 L455.961436,287.712737 L474.616803,287.712737 C475.044739,287.712737 475.379065,287.360434 475.379065,286.940379 L475.379065,264.785908 C475.379065,264.352304 475.031366,264 474.60343,264 Z";
            translateX = -441;
            translateY = -263;
            baseWidth = 36;
            baseHeight = 33;
        }
        else if(patternType == "roundedRectangleBalloon")
        {
            path = "M520.15096,263 L501.152859,263 C497.199963,263 494,266.305556 494,270.388889 L494,283.194444 C494,287.277778 497.199963,290.583333 501.152859,290.583333 L507.848581,290.583333 L510.322502,295 L512.796423,290.583333 L520.15096,290.583333 C524.103856,290.583333 527.303819,287.277778 527.303819,283.194444 L527.303819,270.388889 C527.303819,266.305556 524.090411,263 520.15096,263 Z";
            translateX = -493;
            translateY = -262;
            baseWidth = 36;
            baseHeight = 35;
        }
        else if(patternType == "circleBalloon")
        {
            path = "M562.88474,264 C553.003281,264 545,269.961272 545,277.305671 C545,281.897649 548.125625,285.950207 552.877134,288.343015 L551.531574,294 L557.726758,290.058091 C559.352644,290.417704 561.090659,290.625173 562.88474,290.625173 C572.766198,290.625173 580.769479,284.6639 580.769479,277.319502 C580.769479,269.975104 572.766198,264 562.88474,264 Z";
            translateX = -544;
            translateY = -263;
            baseWidth = 38;
            baseHeight = 34;
        }

        return {
            path : path,
            polygon : polygon,
            translateX : translateX,
            translateY : translateY,
            baseWidth : baseWidth,
            baseHeight : baseHeight
        }
    }
}